import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from "@mui/material";
import { Bolt, ReceiptLong, WarningAmberRounded } from "@mui/icons-material";
import { API_URL } from "const/constants";
import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const fmtMoney = (value) => `$${Number(value || 0).toFixed(2)}`;
const amountOf = (value) => Number(value) || 0;
const getApiErrorMessage = (error) =>
  error?.response?.data?.message || error?.response?.data?.errorMessage || error?.message || "";

/**
 * One-click "direct claim" for a single provider admission term. Two paths:
 *  - Claim now: backend generates a commission+bonus invoice for this term and immediately marks it
 *    paid (claimed, dated today). Zero-value components are auto-claimed without appearing on it.
 *  - Open invoice to edit: generates the same invoice but navigates to it so receipts/details can be
 *    edited before it is marked paid through the normal flow.
 * Both restrict when commission and bonus are both 0 (nothing to claim).
 */
export default function DirectClaimDialog({
  open,
  onClose,
  admission,
  term,
  accountProviderId,
  onClaimed,
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  if (!term) return null;

  const commission = amountOf(term.commission);
  const bonus = amountOf(term.bonus);
  const bothZero = commission <= 0 && bonus <= 0;
  const someZero = commission <= 0 || bonus <= 0;
  const clientName = admission?.clientName || "Client";

  const handleClaimNow = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/provider-admissions/terms/${term.id}/direct-claim`);
      toast.success(`Term ${term.termNumber} claimed`);
      onClaimed?.();
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e) || "Failed to claim term");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenInvoice = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/provider-admissions/invoices/generate`, {
        accountProviderId,
        invoiceType: "COMMISSION_AND_BONUS",
        title: `Term ${term.termNumber} — ${clientName}`,
        clientTermSelections: [{ clientId: admission.clientId, termNumbers: [term.termNumber] }],
      });
      const generated = res?.data?.data;
      const invoice = Array.isArray(generated) ? generated[0] : generated;
      const invoiceId = invoice?.invoiceId;
      if (!invoiceId) {
        toast.error("Invoice was not created");
        return;
      }
      toast.success("Invoice created — add receipts or details, then mark it paid to claim");
      onClose();
      navigate(`/admin/invoice/view/${invoiceId}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e) || "Failed to create invoice");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle className="flex items-center gap-2">
        <Bolt className="text-amber-500" /> Direct Claim — Term {term.termNumber}
      </DialogTitle>
      <DialogContent>
        <p className="text-sm text-app-text mb-2">
          {clientName}
          {admission?.courseName ? ` • ${admission.courseName}` : ""}
        </p>

        <div className="rounded border bg-app-surface-muted p-3 text-sm flex flex-col gap-1">
          <div className="flex justify-between">
            <span className="text-app-text-secondary">Commission</span>
            <span className={commission > 0 ? "font-medium" : "text-app-text-muted"}>
              {fmtMoney(commission)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-app-text-secondary">Bonus</span>
            <span className={bonus > 0 ? "font-medium" : "text-app-text-muted"}>
              {fmtMoney(bonus)}
            </span>
          </div>
          <Divider sx={{ my: 0.5 }} />
          <p className="text-xs text-app-text-secondary">
            Claimed date will be set to <strong>today</strong>.
          </p>
        </div>

        {bothZero ? (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex gap-1 items-start">
            <WarningAmberRounded sx={{ fontSize: 16 }} />
            Both commission and bonus are $0 — there is nothing to claim for this term.
          </div>
        ) : (
          someZero && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex gap-1 items-start">
              <WarningAmberRounded sx={{ fontSize: 16 }} />
              {commission <= 0 ? "Commission" : "Bonus"} is $0 — it won't appear on the invoice but
              will still be marked claimed when this term is settled.
            </div>
          )
        )}

        <p className="mt-3 text-xs text-app-text-muted">
          To upload receipts or change amounts, use <strong>Open invoice to edit</strong> — it
          creates the invoice for this term so you can edit it, then mark it paid to claim.
        </p>
      </DialogContent>
      <DialogActions className="py-2 px-3 gap-2 flex-wrap">
        <Button onClick={onClose} disabled={submitting} variant="outlined" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleOpenInvoice}
          disabled={submitting || bothZero}
          variant="outlined"
          size="small"
          startIcon={<ReceiptLong sx={{ fontSize: 16 }} />}
        >
          Open invoice to edit
        </Button>
        <Button
          onClick={handleClaimNow}
          disabled={submitting || bothZero}
          variant="contained"
          size="small"
          startIcon={submitting ? <CircularProgress size={14} /> : <Bolt sx={{ fontSize: 16 }} />}
        >
          {submitting ? "Working..." : "Claim now"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
